import React from 'react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Shortcut from '@atlaskit/icon/glyph/shortcut';
import Image from '@atlaskit/image';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import {
	srcDark,
	srcLight,
} from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/software/components/magic-search';
import { useIntl } from '@atlassian/jira-intl';
import { useIsAdmin, useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import { CardList, CardListItem } from '../../../common/ui/card-list';
import { TransitionHomeContentBox } from '../../../common/ui/transition-home/content-box';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header';
import { messages as stepMessages } from '../../../common/ui/transition-home/step/messages';
import taskMessages from '../../../common/utils/tasks/messages';
import { useIsTeamAdmin } from '../../../services/store/team-admin-status';
import { messages } from './messages';

export const FindStep = () => {
	const { formatMessage } = useIntl();

	const [{ isTeamAdmin }] = useIsTeamAdmin();
	const isSiteAdmin = useIsSiteAdmin();
	const isPrimaryAdmin = useIsAdmin();

	const showAllAdmins = isSiteAdmin || isPrimaryAdmin || isTeamAdmin;
	const showRealAdmins = isSiteAdmin || isPrimaryAdmin;

	return (
		<Stack space="space.250">
			<TransitionHomeHeader
				title={formatMessage(stepMessages.findWhatYouAreLookingForTitleNonFinal)}
				description={formatMessage(stepMessages.findWhatYouAreLookingForDescriptionNonFinal)}
			/>
			{/* Task One */}
			<TransitionHomeContentBox
				illustration={<Image src={srcLight} srcDark={srcDark} width={200} alt="" />}
			>
				<Stack spread="space-between" space="space.200">
					<Heading as="h2" size="small">
						{formatMessage(taskMessages.findWhatYouAreLookingForTaskOneNonFinal)}
					</Heading>
					<Box xcss={fullWidthStyles}>
						<CardList>
							<CardListItem
								title={formatMessage(messages.yourTeamsTitleNonFinal)}
								description={formatMessage(messages.yourTeamsDescriptionNonFinal)}
								href="/jira/people/search/teams"
								afterIcon={
									<Tooltip content={formatMessage(messages.openTeamsNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<Shortcut size="small" label="" />}
											aria-label={formatMessage(messages.openTeamsNonFinal)}
										/>
									</Tooltip>
								}
							/>
							<CardListItem
								title={formatMessage(messages.schedulesTitleNonFinal)}
								description={formatMessage(messages.schedulesDescriptionNonFinal)}
								href="/jira/ops/who-is-on-call"
								afterIcon={
									<Tooltip content={formatMessage(messages.openSchedulesNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<Shortcut size="small" label="" />}
											aria-label={formatMessage(messages.openSchedulesNonFinal)}
										/>
									</Tooltip>
								}
							/>
							<CardListItem
								title={formatMessage(messages.alertsTitleNonFinal)}
								description={formatMessage(messages.alertsDescriptionNonFinal)}
								href="/jira/ops/alerts"
								afterIcon={
									<Tooltip content={formatMessage(messages.openAlertsNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<Shortcut size="small" label="" />}
											aria-label={formatMessage(messages.openAlertsNonFinal)}
										/>
									</Tooltip>
								}
							/>
							<CardListItem
								title={formatMessage(messages.notificationSettingsTitleNonFinal)}
								description={formatMessage(messages.notificationSettingsDescriptionNonFinal)}
								href="/jira/settings/personal/alert-notifications"
								afterIcon={
									<Tooltip content={formatMessage(messages.openNotificationsNonFinal)}>
										<Button
											appearance="subtle"
											iconBefore={<Shortcut size="small" label="" />}
											aria-label={formatMessage(messages.openNotificationsNonFinal)}
										/>
									</Tooltip>
								}
							/>
							{showAllAdmins && (
								<CardListItem
									title={formatMessage(messages.reportsTitleNonFinal)}
									description={formatMessage(
										isTeamAdmin
											? messages.reportsTeamAdminDescriptionNonFinal
											: messages.reportsDescriptionNonFinal,
									)}
									href={
										isTeamAdmin
											? '/jira/people/search/teams'
											: '/jira/settings/products/ops/reports'
									}
									afterIcon={
										<Tooltip
											content={formatMessage(
												isTeamAdmin ? messages.openTeamsNonFinal : messages.viewReportsNonFinal,
											)}
										>
											<Button
												appearance="subtle"
												iconBefore={<Shortcut size="small" label="" />}
												aria-label={formatMessage(
													isTeamAdmin ? messages.openTeamsNonFinal : messages.viewReportsNonFinal,
												)}
											/>
										</Tooltip>
									}
								/>
							)}
							{showRealAdmins && (
								<CardListItem
									title={formatMessage(messages.auditTitleNonFinal)}
									description={formatMessage(messages.auditDescriptionNonFinal)}
									href="/jira/settings/products/ops/jira-service-management-audit-logs"
									afterIcon={
										<Tooltip content={formatMessage(messages.openAuditLogsNonFinal)}>
											<Button
												appearance="subtle"
												iconBefore={<Shortcut size="small" label="" />}
												aria-label={formatMessage(messages.openAuditLogsNonFinal)}
											/>
										</Tooltip>
									}
								/>
							)}
							{showRealAdmins && (
								<CardListItem
									title={formatMessage(messages.rbnTitleNonFinal)}
									description={formatMessage(messages.rbnDescriptionNonFinal)}
									href="/jira/settings/products/ops/role-based-notifications"
									afterIcon={
										<Tooltip content={formatMessage(messages.openRbnNonFinal)}>
											<Button
												appearance="subtle"
												iconBefore={<Shortcut size="small" label="" />}
												aria-label={formatMessage(messages.openRbnNonFinal)}
											/>
										</Tooltip>
									}
								/>
							)}
							{showAllAdmins && (
								<CardListItem
									title={formatMessage(
										isTeamAdmin
											? messages.customTeamRolesTitleNonFinal
											: messages.customRolesTitleNonFinal,
									)}
									description={formatMessage(
										isTeamAdmin
											? messages.customRolesTeamAdminDescriptionNonFinal
											: messages.customRolesDescriptionNonFinal,
									)}
									href={
										isTeamAdmin ? '/jira/people/search/teams' : '/jira/settings/products/ops/roles'
									}
									afterIcon={
										<Tooltip
											content={formatMessage(
												isTeamAdmin ? messages.openTeamsNonFinal : messages.openCustomRolesNonFinal,
											)}
										>
											<Button
												appearance="subtle"
												iconBefore={<Shortcut size="small" label="" />}
												aria-label={formatMessage(
													isTeamAdmin
														? messages.openTeamsNonFinal
														: messages.openCustomRolesNonFinal,
												)}
											/>
										</Tooltip>
									}
								/>
							)}
							{showAllAdmins && (
								<CardListItem
									title={formatMessage(messages.automationTitleNonFinal)}
									description={formatMessage(
										isTeamAdmin
											? messages.automationTeamAdminDescriptionNonFinal
											: messages.automationDescriptionNonFinal,
									)}
									href={isTeamAdmin ? '/jira/people/search/teams' : '/jira/settings/automation'}
									afterIcon={
										<Tooltip
											content={formatMessage(
												isTeamAdmin ? messages.openTeamsNonFinal : messages.openAutomationNonFinal,
											)}
										>
											<Button
												appearance="subtle"
												iconBefore={<Shortcut size="small" label="" />}
												aria-label={formatMessage(
													isTeamAdmin
														? messages.openTeamsNonFinal
														: messages.openAutomationNonFinal,
												)}
											/>
										</Tooltip>
									}
								/>
							)}
							{showAllAdmins && (
								<CardListItem
									title={formatMessage(messages.syncTitleNonFinal)}
									description={formatMessage(
										isTeamAdmin
											? messages.syncTeamAdminDescriptionNonFinal
											: messages.syncDescriptionNonFinal,
									)}
									href={
										isTeamAdmin ? '/jira/people/search/teams' : '/jira/settings/products/ops/syncs'
									}
									afterIcon={
										<Tooltip
											content={formatMessage(
												isTeamAdmin ? messages.openTeamsNonFinal : messages.openSyncNonFinal,
											)}
										>
											<Button
												appearance="subtle"
												iconBefore={<Shortcut size="small" label="" />}
												aria-label={formatMessage(
													isTeamAdmin ? messages.openTeamsNonFinal : messages.openSyncNonFinal,
												)}
											/>
										</Tooltip>
									}
								/>
							)}
						</CardList>
					</Box>
				</Stack>
			</TransitionHomeContentBox>
		</Stack>
	);
};

const fullWidthStyles = xcss({
	width: '100%',
});
